import { useCallback } from 'react';
import { usePermission } from '../../hooks/common/usePermission.ts';
import type { AccessPermissionKey } from '@qcdoc/mock-runtime/contracts/accessModel.ts';

export function routePermission(path: string): AccessPermissionKey | undefined {
  if (path === '/products/stp/new') return 'STP.Create';
  if (/^\/products\/stp\/[^/]+\/edit$/.test(path)) return 'STP.Edit';
  if (path.startsWith('/products/stp')) return 'STP.View';
  if (/^\/products\/[^/]+\/edit$/.test(path)) return 'Products.Edit';
  if (path.startsWith('/products/report') || path.startsWith('/products/approved-suppliers')) return 'Reports.View';
  if (path === '/products/new') return 'Products.Create';
  if (path.startsWith('/products')) return 'Products.View';
  if (path.startsWith('/vqca/gql-pending')) return 'VQCA.SignGql';
  if (path.startsWith('/vqca/cm-pending')) return 'VQCA.SignCm';
  if (path.startsWith('/vqca/requests/new')) return 'VQCARequests.Create';
  if (path === '/vqca/requests/cm-pending' || path === '/vqca/requests/vqca-pending') return 'VQCARequests.Approve';
  if (path.startsWith('/vqca/requests')) return 'VQCARequests.View';
  if (path.startsWith('/vqca/new')) return 'VQCA.Create';
  if (path.startsWith('/vqca')) return 'VQCA.View';
  if (path.startsWith('/coa/new')) return 'COA.Create';
  if (/^\/coa\/[^/]+\/edit$/.test(path)) return 'COA.Edit';
  if (path.startsWith('/coa/deleted')) return 'COA.Delete';
  if (path.startsWith('/coa/under-review')) return 'COA.Review';
  if (path.startsWith('/coa')) return 'COA.View';
  if (path.startsWith('/qa-lab/samples/new')) return 'Samples.Create';
  if (/^\/qa-lab\/samples\/[^/]+\/edit$/.test(path)) return 'Samples.Edit';
  if (/^\/qa-lab\/samples\/[^/]+\/experiments\/new$/.test(path)) return 'Experiments.Create';
  if (/^\/qa-lab\/samples\/[^/]+\/report$/.test(path)) return 'QAReports.View';
  if (path.startsWith('/qa-lab/experiments')) return 'Experiments.View';
  if (path === '/qa-lab/unapproved') return 'QAReports.Approve';
  if (path === '/qa-lab/failed-experiments') return 'Experiments.Approve';
  if (path === '/qa-lab/manage' || path === '/qa-lab/test-certificates') return 'QAReports.View';
  if (path.startsWith('/qa-lab')) return 'Samples.View';
  if (path === '/coq/deleted') return 'COQ.Delete';
  if (path === '/coq/generate' || path === '/coq/lot-search') return 'COQ.Generate';
  if (path === '/coq/sample-search') return 'Samples.View';
  if (path.startsWith('/coq')) return 'COQ.View';
  if (path.startsWith('/packaging/new')) return 'Packaging.Create';
  if (path.startsWith('/packaging')) return 'Packaging.View';
  if (path.startsWith('/hdfx')) return 'Hdfx.View';
  if (['/reports/metrics', '/reports/missing-po-summary', '/reports/sap-po-log', '/reports/unlisted-material-exception', '/reports/missing-po-exception', '/reports/unlisted-vendor-exception'].includes(path)) return 'Reports.AdminView';
  if (path.startsWith('/reports')) return 'Reports.View';
  if (path === '/vendors/new' || /^\/vendors\/[^/]+\/(?:edit|users\/bulk)$/.test(path)) return 'Vendors.Manage';
  if (path.startsWith('/vendors')) return 'Vendors.View';
  if (path.startsWith('/alerts') || path.startsWith('/admin/alerts')) return 'Alerts.View';
  if (path.startsWith('/categories') || path.startsWith('/admin/category-levels')) return 'CategoryLevels.View';
  if (path === '/admin/users/new' || path.startsWith('/admin/user-requests')) return 'Administration.Users.Manage';
  if (path.startsWith('/admin/users')) return 'Administration.Users.View';
  if (path.startsWith('/admin/role-access') || path === '/admin/roles') return 'Administration.Roles.View';
  if (path.startsWith('/admin/reference-data')) return 'ReferenceData.View';
  return undefined;
}

export function routePermissions(path: string): AccessPermissionKey[] {
  const pathname = path.split(/[?#]/, 1)[0].replace(/\/+$/, '') || '/';
  if (pathname === '/admin/role-access' || pathname === '/admin/roles') return ['Administration.Roles.View', 'Administration.Roles.Manage'];
  if (pathname === '/admin') return ['Administration.Users.View', 'Administration.Users.Manage', 'Administration.Roles.View', 'Administration.Roles.Manage',
    'ReferenceData.View', 'CategoryLevels.View', 'Alerts.View'];
  const permission = routePermission(pathname);
  return permission ? [permission] : [];
}

/** Resolve internal module routes against the same permission flags used by navigation. */
export function useInternalRouteAccess() {
  const permission = usePermission();

  return useCallback((path: string) => {
    const pathname = path.split(/[?#]/, 1)[0].replace(/\/+$/, '') || '/';

    if (pathname === '/') return true;
    if (permission.hasAccessProfile) return routePermissions(pathname).some((key) => permission.hasPermission(key));
    if (pathname.startsWith('/products/stp')) return permission.canViewStp;
    if (pathname.startsWith('/products/report') || pathname.startsWith('/products/approved-suppliers')) return permission.canViewProductReports;
    if (pathname.startsWith('/products')) return permission.canViewProducts;
    if (pathname.startsWith('/vqca')) return permission.canManageVqca;
    if (pathname.startsWith('/coa')) return permission.canManageCoa;
    if (pathname.startsWith('/qa-lab')) return permission.canAccessQALab;
    if (pathname.startsWith('/coq')) return permission.canAccessLogistics;
    if (pathname.startsWith('/packaging')) return permission.canManagePackaging;
    if (pathname.startsWith('/alerts')) return permission.canManageAlerts;
    if (pathname.startsWith('/categories')) return permission.canManageCategories;
    if (pathname.startsWith('/hdfx')) return permission.canAccessHdfx;
    if (pathname.startsWith('/reports')) return permission.canViewReports;
    if (pathname.startsWith('/vendors')) return permission.canViewVendors;
    if (pathname.startsWith('/admin')) return permission.isAdmin;

    return false;
  }, [permission]);
}